// 간편결제 복귀 · 콜드 in-flight 복구 부트스트랩.
//
// 복귀 신호(onEasyPayReturn)는 결제 스텝 소비자가 있으면 제자리 처리하고, 없으며 이미 결과 라우트에 있지도 않을
// 때만 /payment/return 으로 이동한다. 앱 로드 시엔 오래된 in-flight 잔재만 정리하며 자동 재유도하지 않는다.
// 콜드 폴백의 폴링 UI 는 그 라우트(콜드스타트-safe)가 소유한다 — 모든 authenticated route 에서 전역 폴링을 시작하지 않는다(Codex #8).
// 콜드스타트-safe 전이라 client nav 대신 window.location 전이를 쓴다(App 은 Router 위라 useNavigate 부재).
import {
  registerEasyPayReturnHandler,
  getEasyPayInPlaceConsumer,
} from "./easyPayReturn";
import { readInflightPayment, clearInflightPayment } from "./paymentInflight";
import { ROUTES } from "@/shared/config/routes";

// 결제 시작 후 30분 초과 in-flight 는 잔재로 보고 정리(재폴링 무한루프 방지).
const INFLIGHT_STALE_MS = 30 * 60 * 1000;

const isOnPaymentRoute = (): boolean => {
  const path = window.location.pathname;
  return (
    path.startsWith(ROUTES.PAYMENT_RETURN) || path.startsWith(ROUTES.EXAM_PAYMENT_RESULT)
  );
};

const goToPaymentReturn = (orderId: string): void => {
  // warm(결제 스텝 생존) 이면 리로드 없이 그 자리에서 처리 — iOS/AOS 동작 통일.
  const consumer = getEasyPayInPlaceConsumer();
  if (consumer) {
    consumer(orderId);
    return;
  }
  // cold 폴백(스텝 미마운트 / cold-start 버퍼 신호): 폴링 라우트로 리로드.
  if (isOnPaymentRoute()) return; // 이미 폴링 라우트면 재유도 안 함(중복 전이 방지)
  window.location.assign(`${ROUTES.PAYMENT_RETURN}?orderId=${encodeURIComponent(orderId)}`);
};

/**
 * 부트스트랩(App)에서 1회 호출. 반환 cleanup 을 effect 에서 반납한다.
 * 1) onEasyPayReturn 복귀 신호 → 결제 스텝 소비자가 있으면 제자리 처리, 없으면(결과 라우트 밖일 때만)
 *    /payment/return 로 유도(examId 는 그 라우트가 in-flight 로 매칭).
 * 2) 오래된(30분 초과) 미확정 in-flight 잔재는 정리한다.
 *
 * [임시] 콜드 재시작만으로는 미확정 in-flight 를 /payment/return 에 자동 재유도하지 않는다 — 이탈 결제가 재실행마다 확인 화면에 갇히는 문제 회피. in-flight 는 302 결과페이지·onEasyPayReturn 복귀를 위해 유지한다. 콜백 없는 콜드 재시작의 자동 완료 복구는 포기하며, 완료 여부는 시험 목록·서버 확정으로 확인한다(이탈 UX 확정 후 재검토).
 */
export const bootstrapEasyPayReturn = (): (() => void) => {
  const cleanup = registerEasyPayReturnHandler(goToPaymentReturn);

  // 오래된 잔재만 정리 — 콜드 재시작 자동 재유도는 하지 않는다(위 임시 주석).
  const inflight = readInflightPayment();
  if (inflight && Date.now() - inflight.startedAt > INFLIGHT_STALE_MS) {
    clearInflightPayment();
  }

  return cleanup;
};
